import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { 
  Box, 
  Paper, 
  Typography, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  Alert,
  CircularProgress
} from '@mui/material';
import { RootState } from '../../app/store';
import TemplateManagement from '../../components/templates/TemplateManagement';
import { api } from '../../services/api';

interface Brand {
  brand_id: string;
  brand_name: string;
  platform: string;
}

const TemplateManagementPage: React.FC = () => {
  const [brands, setBrands] = useState<Brand[]>([]);
  const [selectedBrand, setSelectedBrand] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useSelector((state: RootState) => state.auth);

  useEffect(() => {
    fetchBrands();
  }, []);

  const fetchBrands = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/feedback/brands');
      const allBrands = response.data.data;
      
      // Filter brands to include all platforms for template management
      const uniqueBrands = allBrands.reduce((acc: Brand[], brand: Brand) => {
        if (!acc.find(b => b.brand_id === brand.brand_id)) {
          acc.push({
            brand_id: brand.brand_id,
            brand_name: brand.brand_name,
            platform: 'all' // Templates can be used across platforms
          });
        }
        return acc;
      }, []);
      
      setBrands(uniqueBrands);
      
      // Auto-select first brand if available
      if (uniqueBrands.length > 0) {
        setSelectedBrand(uniqueBrands[0].brand_id);
      }
    } catch (error) {
      console.error('Error fetching brands:', error);
      setError('Failed to load brands');
    } finally {
      setLoading(false);
    }
  };

  const selectedBrandInfo = brands.find(b => b.brand_id === selectedBrand);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (brands.length === 0) {
    return (
      <Alert severity="info" sx={{ mb: 2 }}>
        No brands available. Please contact your administrator.
      </Alert>
    );
  }

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        Template Management
      </Typography>
      
      <Paper sx={{ p: 2, mb: 3 }}>
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Select Brand</InputLabel>
          <Select
            value={selectedBrand}
            onChange={(e) => setSelectedBrand(e.target.value)}
            label="Select Brand"
          >
            {brands.map((brand) => (
              <MenuItem key={brand.brand_id} value={brand.brand_id}>
                {brand.brand_name} ({brand.brand_id})
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        
        <Typography variant="body2" color="text.secondary">
          Manage message templates for {selectedBrandInfo?.brand_name}. 
          Templates can be used across different platforms including Instagram, TikTok, and Google Reviews.
        </Typography>
      </Paper>

      {selectedBrand && (
        <TemplateManagement 
          brandId={selectedBrand}
          brandName={selectedBrandInfo?.brand_name}
        />
      )}
    </Box>
  );
};

export default TemplateManagementPage;
